/**
 * Plain-language rendering of a job's schedule, plus its next occurrence.
 *
 * A raw EventBridge cron (`cron(0 7 * * ? *)`) is meaningless to a human and
 * unreadable on a phone, so the Jobs panel shows prose instead: "Daily at
 * 7:00 AM · next run tomorrow, 7:00 AM".
 *
 * **Timezone semantics (matches infra).** The jobs Lambda passes the schedule's
 * `timeZone` to EventBridge Scheduler as `ScheduleExpressionTimezone`, so the
 * cron's hour/minute fields are wall-clock times IN THAT ZONE (UTC when the job
 * carries none). We interpret them that way and render the result in the
 * VIEWER's zone — the two differ whenever a user travels or schedules for
 * another region.
 *
 * **Deliberately narrow.** We render only the cron shapes our own scheduler
 * emits (fixed minute+hour, daily / weekly-by-weekday / monthly-by-day). Any
 * other expression returns "Custom schedule" with no next-run guess rather
 * than risk telling the user a confident wrong time.
 *
 * @module agents/scheduleText
 */

import type { JobDefinition } from './jobsApi.ts';

/** What the Jobs row renders for one schedule. */
export interface ScheduleText {
  /** e.g. "Daily at 7:00 AM". Never empty. */
  readonly summary: string;
  /** e.g. "next run tomorrow, 7:00 AM". Null when unknown or in the past. */
  readonly next: string | null;
}

/** Wall-clock instant, as the schedule's own timezone sees it. */
interface WallClock {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
}

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/**
 * EventBridge accepts both names and 1-7 (Sunday = 1) for day-of-week.
 * Typed as possibly-undefined because the lookup key comes from a cron
 * expression we did not write — an unknown token must be detectable.
 */
const WEEKDAY_TOKENS: Record<string, number | undefined> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
  '1': 0,
  '2': 1,
  '3': 2,
  '4': 3,
  '5': 4,
  '6': 5,
  '7': 6,
};

/** The six fields of an EventBridge cron, unwrapped from `cron(...)`. */
interface CronFields {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
  year: string;
}

/** Parse `cron(m h dom mon dow yr)`. Null for anything else. */
function parseCron(expression: string): CronFields | null {
  const match = /^cron\((.+)\)$/.exec(expression.trim());
  if (match === null) {
    return null;
  }
  const parts = match[1].trim().split(/\s+/);
  if (parts.length !== 6) {
    return null;
  }
  const [minute, hour, dayOfMonth, month, dayOfWeek, year] = parts;
  return { minute, hour, dayOfMonth, month, dayOfWeek, year };
}

/** A field is "any" when it is `*` or the cron `?` no-op. */
function isAny(field: string): boolean {
  return field === '*' || field === '?';
}

/** "7:00 AM" from 24h parts. */
function clockLabel(hour: number, minute: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(h12)}:${String(minute).padStart(2, '0')} ${suffix}`;
}

/**
 * The offset-corrected epoch instant for a wall-clock time in `timeZone`.
 *
 * `Date.UTC` gives the instant as if the wall clock were UTC; formatting that
 * instant back in the target zone reveals how far off the zone is, and adding
 * the delta lands on the true instant. One correction pass handles every
 * standard offset and DST shift except the ambiguous hour of a fall-back
 * transition, where either candidate is a defensible answer.
 */
function zonedWallClockToInstant(wall: WallClock, timeZone: string): number {
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  const seenInZone = wallClockInZone(new Date(asUtc), timeZone);
  const seenAsUtc = Date.UTC(
    seenInZone.year,
    seenInZone.month - 1,
    seenInZone.day,
    seenInZone.hour,
    seenInZone.minute,
  );
  return asUtc + (asUtc - seenAsUtc);
}

/** What `timeZone`'s wall clock reads at `instant`. */
function wallClockInZone(instant: Date, timeZone: string): WallClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(instant);
  const value = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');
  // Intl renders midnight as hour 24 in some engines; normalise it.
  const hour = value('hour') % 24;
  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    hour,
    minute: value('minute'),
  };
}

/** Whether a candidate day satisfies the cron's day-of-month / day-of-week. */
function dayMatches(fields: CronFields, candidate: Date, timeZone: string): boolean {
  const wall = wallClockInZone(candidate, timeZone);
  if (!isAny(fields.dayOfMonth) && Number(fields.dayOfMonth) !== wall.day) {
    return false;
  }
  if (!isAny(fields.dayOfWeek)) {
    const wanted = fields.dayOfWeek
      .split(',')
      .map((token) => WEEKDAY_TOKENS[token.trim().toUpperCase()])
      .filter((d): d is number => d !== undefined);
    if (wanted.length === 0) {
      return false;
    }
    // `getUTCDay` on the zone's wall-clock date gives that date's weekday.
    const weekday = new Date(Date.UTC(wall.year, wall.month - 1, wall.day)).getUTCDay();
    if (!wanted.includes(weekday)) {
      return false;
    }
  }
  return true;
}

/** Human phrase for how often the cron fires. Null when we can't read it. */
function summarizeCron(fields: CronFields): string | null {
  const minute = Number(fields.minute);
  const hour = Number(fields.hour);
  // Fixed time-of-day only: a wildcard or step in minute/hour means "many times
  // a day", which this renderer does not attempt to phrase.
  if (!Number.isInteger(minute) || !Number.isInteger(hour)) {
    return null;
  }
  if (!isAny(fields.month) || !isAny(fields.year)) {
    return null;
  }
  const at = clockLabel(hour, minute);

  if (!isAny(fields.dayOfWeek)) {
    const days = fields.dayOfWeek
      .split(',')
      .map((token) => WEEKDAY_TOKENS[token.trim().toUpperCase()])
      .filter((d): d is number => d !== undefined);
    if (days.length === 0) {
      return null;
    }
    if (days.length === 1) {
      return `${WEEKDAY_NAMES[days[0]]}s at ${at}`;
    }
    return `${days.map((d) => WEEKDAY_NAMES[d].slice(0, 3)).join(', ')} at ${at}`;
  }

  if (!isAny(fields.dayOfMonth)) {
    const day = Number(fields.dayOfMonth);
    if (!Number.isInteger(day)) {
      return null;
    }
    return `Monthly on day ${String(day)} at ${at}`;
  }

  return `Daily at ${at}`;
}

/** Epoch ms of the first firing strictly after `now`, or null if unreadable. */
function nextOccurrence(fields: CronFields, timeZone: string, now: Date): number | null {
  const minute = Number(fields.minute);
  const hour = Number(fields.hour);
  if (!Number.isInteger(minute) || !Number.isInteger(hour)) {
    return null;
  }
  const startWall = wallClockInZone(now, timeZone);
  // Walk forward a day at a time. 400 covers "day 31 of a month" in any year.
  for (let offset = 0; offset < 400; offset += 1) {
    const dayUtc = new Date(
      Date.UTC(startWall.year, startWall.month - 1, startWall.day + offset),
    );
    const wall: WallClock = {
      year: dayUtc.getUTCFullYear(),
      month: dayUtc.getUTCMonth() + 1,
      day: dayUtc.getUTCDate(),
      hour,
      minute,
    };
    const instant = zonedWallClockToInstant(wall, timeZone);
    if (instant <= now.getTime()) {
      continue; // today's firing already passed
    }
    if (!dayMatches(fields, new Date(instant), timeZone)) {
      continue;
    }
    return instant;
  }
  return null;
}

/** "tomorrow, 7:00 AM" / "Mon Aug 4, 6:30 AM" — in the VIEWER's zone. */
function relativeLabel(instant: number, now: Date): string {
  const when = new Date(instant);
  const time = when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const dayDelta = Math.round(
    (new Date(when.getFullYear(), when.getMonth(), when.getDate()).getTime() -
      new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) /
      86_400_000,
  );
  if (dayDelta === 0) {
    return `today, ${time}`;
  }
  if (dayDelta === 1) {
    return `tomorrow, ${time}`;
  }
  const date = when.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return `${date}, ${time}`;
}

/**
 * Render a job's schedule as prose plus its next run.
 *
 * `now` is injected rather than read from the clock so this stays a pure
 * function — the unit tests pin it, and the panel passes `new Date()`.
 */
export function describeScheduleText(def: JobDefinition, now: Date): ScheduleText {
  if (def.schedule.type === 'one-time') {
    // Stored without an offset ("YYYY-MM-DDTHH:MM"), interpreted in the job's
    // zone by EventBridge — parse it the same way.
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(def.schedule.at);
    if (match === null) {
      return { summary: 'Once (scheduled)', next: null };
    }
    const [, year, month, day, hour, minute] = match;
    const instant = zonedWallClockToInstant(
      {
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
        minute: Number(minute),
      },
      def.timeZone ?? 'UTC',
    );
    const when = new Date(instant);
    const label = `${when.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })}, ${when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
    return {
      summary: `Once on ${label}`,
      next: instant > now.getTime() ? `next run ${relativeLabel(instant, now)}` : null,
    };
  }

  const fields = parseCron(def.schedule.cron);
  if (fields === null) {
    return { summary: 'Custom schedule', next: null };
  }
  const summary = summarizeCron(fields);
  if (summary === null) {
    return { summary: 'Custom schedule', next: null };
  }
  const zone = def.timeZone ?? 'UTC';
  const instant = nextOccurrence(fields, zone, now);
  return {
    summary,
    next: instant === null ? null : `next run ${relativeLabel(instant, now)}`,
  };
}
